'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { EntityMenu } from '@nx-frontend/shared/ui';
import { EntityMenuAction, EntityMenuItem } from '@nx-frontend/shared/ui/entity-menu';
import { generalEnv } from '@nx-frontend/shared/env';
import { useOpenCreateWorkspaceDialog } from '@nx-frontend/shared/hooks/use-create-workspace';
import { useCustomizerCheckChanges } from '@nx-frontend/shared/hooks/use-customizer-check-changes';
import { $workspacesAvatars } from '@nx-frontend/shared/store/workspace-avatars';
import { $workspaces } from '@nx-frontend/shared/store/workspaces';
import { WorkspaceAvatar } from '@nx-frontend/shared/types';

export function WorkspaceSelectList({
  workspaceId,
  actions,
  select,
  onOpenCreatePopup,
}: {
  workspaceId: string;
  select?: (id: string) => void;
  actions?: EntityMenuAction[];
  onOpenCreatePopup?: () => void;
}) {
  const [workspaces, workspacesAvatar] = useUnit([$workspaces, $workspacesAvatars]);
  const t = useTranslations('features');
  const openCreateWorkspaceDialog = useOpenCreateWorkspaceDialog();
  const customizerCheckChanges = useCustomizerCheckChanges();
  const items: EntityMenuItem[] = useMemo(() => {
    return workspaces.reduce((acc, workspace) => {
      const workspaceAvatar: WorkspaceAvatar | undefined = workspacesAvatar.find((workspaceAvatar) => {
        return workspaceAvatar.workspaceId === workspace.globalId;
      });
      const item = {
        name: workspace.title,
        avatarUrl: workspaceAvatar?.storedFileUUID
          ? `${generalEnv.staticAssetPrefix}${workspaceAvatar?.storedFileUUID}`
          : undefined,
        id: workspace.globalId,
        action: () => {
          if (select) {
            select(workspace.globalId);
          }
        },
      };

      if (item.id === workspaceId) {
        acc.unshift(item);
      } else {
        acc.push(item);
      }

      return acc;
    }, [] as EntityMenuItem[]);
  }, [select, workspaceId, workspaces, workspacesAvatar]);

  const handleCreate = () => {
    if (onOpenCreatePopup) {
      onOpenCreatePopup();
    }
    customizerCheckChanges(() => {
      openCreateWorkspaceDialog();
    });
  };
  return (
    <EntityMenu
      items={items}
      createText={t('workspace-select.create_new_workspace')}
      actions={actions}
      onCreate={handleCreate}
    />
  );
}
